import { useEffect, useRef, useState, useCallback } from 'react'
import { Message } from '@/lib/types'
import { MessageItem } from './MessageItem'
import { EmptyState } from './EmptyState'
import { ChevronDown } from 'lucide-react'

interface MessageListProps {
  messages: Message[]
  isStreaming: boolean
}

export function MessageList({ messages, isStreaming }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)

  // Check if user is at the bottom of the scroll
  const isAtBottom = useCallback(() => {
    if (!containerRef.current) return false
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollHeight - scrollTop - clientHeight < 50 // 50px threshold
  }, [])

  // Handle scroll events to detect user scrolling
  const handleScroll = useCallback(() => {
    const atBottom = isAtBottom()
    setIsUserScrolledUp(!atBottom)
    setShowScrollToBottom(!atBottom && messages.length > 0)
  }, [isAtBottom, messages.length])

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setIsUserScrolledUp(false)
    setShowScrollToBottom(false)
  }, [])

  // Smart auto-scroll: only scroll when user is at bottom or it's a new conversation
  useEffect(() => {
    if (!isUserScrolledUp || messages.length <= 1) {
      // Use requestAnimationFrame to avoid excessive scrolling during streaming
      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [messages, isUserScrolledUp])

  if (messages.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="relative h-full">
      <div 
        ref={containerRef}
        className="h-full overflow-y-auto overflow-x-hidden custom-scrollbar bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/50 dark:to-gray-800"
        onScroll={handleScroll}
      >
        <div className="max-w-4xl mx-auto py-6 px-4 space-y-1">
          {messages
            .filter((message, index) => {
              // Hide empty assistant messages during streaming (thinking steps will be shown in MessageItem)
              const isEmptyAssistant = message.role === 'assistant' && !message.content && !message.thinkingSteps
              const isLastMessage = isStreaming && index === messages.length - 1
              return !(isEmptyAssistant && isLastMessage)
            })
            .map((message, index) => {
              return (
                <MessageItem
                  key={message.id}
                  message={message}
                  isStreaming={isStreaming && index === messages.length - 1 && message.role === 'assistant'}
                />
              )
            })}
          
          <div ref={messagesEndRef} className="h-4" />
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollToBottom && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-20 right-6 z-10 bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
          aria-label="Scroll to bottom"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      )}
    </div>
  )
}

